import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PROFILE_VERTICAL_CONFIGS,
  type ProfileVertical,
} from '@photofeeler/shared';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/AuthModal';
import { HeroDemo } from '../components/HeroDemo';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { JsonLd } from '../components/JsonLd';
import { EditorialFlow, ProfilePreviewCard } from '../components/pretext';
import { buildFontString } from '../lib/pretext';

const FAKE_STEPS = [
  'Evaluating facial symmetry...',
  'Analyzing lighting quality...',
  'Assessing expression warmth...',
  'Scoring background composition...',
];

const DATING_FAQ = [
  { q: 'What makes a good dating lead photo?', a: 'A photo with genuine warmth, direct eye contact, and natural lighting. The best lead photos make someone pause mid-swipe and want to learn more about you.' },
  { q: 'How many photos should I compare?', a: 'At least 3-5 for a reliable comparison. Most users find their strongest lead photo after comparing their top 4-6 options.' },
  { q: 'Will this help me get more matches?', a: 'Yes. Users who switch to their highest-scored photo report 2-4x more matches on average. The key is testing, not guessing.' },
];

const LINKEDIN_FAQ = [
  { q: 'What makes a good LinkedIn headshot?', a: 'A clean background, professional attire, and a confident but approachable expression. Recruiters judge competence in the first 100 milliseconds.' },
  { q: 'Should I smile in my LinkedIn photo?', a: 'A slight, natural smile projects approachability without undermining authority. Neutral expressions score lower on trust.' },
  { q: 'How often should I update my headshot?', a: 'Every 1-2 years, or whenever your appearance, role, or seniority changes significantly. An outdated photo erodes trust.' },
];

function VerticalFaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  }, []);

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} className="card overflow-hidden">
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
              aria-expanded={isOpen}
            >
              <span className="font-display text-base text-ink" style={{ fontWeight: 500 }}>{item.q}</span>
              <svg
                className="shrink-0 w-5 h-5 text-ink-faint transition-transform duration-300"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-sm text-ink-muted font-ui leading-relaxed animate-fade-in" style={{ fontWeight: 300 }}>
                {item.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface VerticalLandingProps {
  vertical: ProfileVertical;
}

export function VerticalLanding({ vertical }: VerticalLandingProps) {
  const config = PROFILE_VERTICAL_CONFIGS[vertical];
  const isDating = vertical === 'dating';
  const accentText = isDating ? 'text-rose' : 'text-info';
  const accentChip = isDating ? 'bg-rose-faint text-rose border-rose/20' : 'bg-info/10 text-info border-info/20';
  const navigate = useNavigate();
  const { isAuthenticated, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showFakeAnalysis, setShowFakeAnalysis] = useState(false);
  const [showBlurredResults, setShowBlurredResults] = useState(false);
  const [fakeStep, setFakeStep] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  usePageMetadata({
    title: `${config.label} Photo Analyzer — PhotoFeeler AI`,
    description: config.description,
    canonicalPath: config.landingPath,
  });

  const handleGoogleSignIn = async () => {
    if (pendingFile) {
      sessionStorage.setItem('postAuthRoute', '/upload');
    }
    await signInWithGoogle();
  };

  const handleCTA = () => {
    // Both auth and unauth users pick a file first.
    // Auth: file → navigate to /upload immediately.
    // Unauth: file → fake analysis → auth modal → navigate to /upload.
    fileRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isAuthenticated) {
      navigate('/upload', { state: { file, preselectedVertical: vertical } });
      return;
    }

    setPendingFile(file);
    setShowFakeAnalysis(true);
    setFakeStep(0);

    const stepInterval = setInterval(() => {
      setFakeStep(previousStep => {
        if (previousStep >= FAKE_STEPS.length - 1) {
          clearInterval(stepInterval);
          return previousStep;
        }
        return previousStep + 1;
      });
    }, 700);

    setTimeout(() => {
      clearInterval(stepInterval);
      setShowFakeAnalysis(false);
      setShowBlurredResults(true);
      setTimeout(() => setAuthOpen(true), 2000);
    }, 3000);
  };

  const handleAuthComplete = () => {
    if (pendingFile) {
      navigate('/upload', { state: { file: pendingFile, preselectedVertical: vertical } });
      setPendingFile(null);
      return;
    }

    navigate('/app');
  };

  /* ── Workflow section (shared between verticals, with fake analysis overlay) ── */
  const workflowSection = (
    <section className={`px-5 sm:px-8 max-w-5xl mx-auto ${isDating ? 'pb-20' : 'pb-24'}`}>
      <div className="marketing-panel-warm p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">{config.emoji}</span>
          <span className="text-sm font-sans font-medium text-ink">{config.label} workflow</span>
        </div>

        {showFakeAnalysis ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-muted">Analyzing your preview...</p>
            <div className="text-base font-sans text-ink">{FAKE_STEPS[fakeStep]}</div>
          </div>
        ) : showBlurredResults ? (
          <div className="space-y-3">
            <div className="rounded-[28px] border border-surface-border bg-surface p-6 blur-[2px] shadow-card">
              <div className="text-5xl font-sans text-blue-600">78</div>
              <div className="text-sm text-ink-muted mt-2">
                Best photo selected. Three high-impact improvements ready.
              </div>
            </div>
            <p className="text-sm text-ink-muted">
              Create your account to unlock the full {config.label} result.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                What you get
              </div>
              <ul className="space-y-2 text-sm text-ink-muted">
                {isDating ? (
                  <>
                    <li>Best lead photo recommendation</li>
                    <li>Why it stops the swipe</li>
                    <li>What to fix in weaker lineup photos</li>
                    <li>Retake checklist for the next version</li>
                  </>
                ) : (
                  <>
                    <li>Best headshot recommendation</li>
                    <li>Why it projects the right impression</li>
                    <li>What to fix in weaker alternatives</li>
                    <li>Retake checklist for the next version</li>
                  </>
                )}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                Built for this goal
              </div>
              <p className="text-sm text-ink-muted">{config.description}</p>
              <div className={`mt-5 rounded-[24px] border p-4 ${isDating ? 'border-rose/15 bg-rose-faint' : 'border-info/15 bg-info/5'}`}>
                <div className={`text-[11px] uppercase tracking-[0.22em] mb-2 ${accentText}`}>Premium path</div>
                <p className="text-sm text-ink-muted leading-relaxed">
                  {isDating
                    ? 'Start with photo selection, then tighten the bio, prompts, and photo order so the whole profile feels intentional.'
                    : 'Start with headshot selection, then refine headline, About section, and banner so the whole profile projects seniority.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );

  const webPageData = isDating
    ? {
        '@context': 'https://schema.org' as const,
        '@type': 'WebPage' as const,
        'name': 'Dating Photo Analyzer',
        'description': 'Find your best Hinge, Tinder, or Bumble photo.',
        'url': 'https://photofeeler.ai/dating',
      }
    : {
        '@context': 'https://schema.org' as const,
        '@type': 'WebPage' as const,
        'name': 'LinkedIn Photo Analyzer',
        'description': 'Choose the headshot that makes the strongest professional impression.',
        'url': 'https://photofeeler.ai/linkedin',
      };

  const faqItems = isDating ? DATING_FAQ : LINKEDIN_FAQ;

  const faqPageData = {
    '@context': 'https://schema.org' as const,
    '@type': 'FAQPage' as const,
    'mainEntity': faqItems.map(item => ({
      '@type': 'Question' as const,
      'name': item.q,
      'acceptedAnswer': {
        '@type': 'Answer' as const,
        'text': item.a,
      },
    })),
  };

  return (
    <div className="relative overflow-hidden bg-cream min-h-screen">
      <JsonLd data={webPageData} />
      <JsonLd data={faqPageData} />
      <AuthModal
        isOpen={authOpen}
        onClose={() => {
          setAuthOpen(false);
          setShowBlurredResults(false);
          setPendingFile(null);
        }}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailSignIn={signInWithEmail}
        onEmailSignUp={signUpWithEmail}
        customHeading={pendingFile ? `${config.label} analysis is ready` : undefined}
        customSubtext={
          pendingFile ? 'Sign up to save this project and unlock 3 free credits.' : undefined
        }
        onAuthComplete={handleAuthComplete}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className={`pointer-events-none absolute -left-20 top-24 h-[22rem] w-[22rem] rounded-full blur-3xl ${isDating ? 'bg-rose/15' : 'bg-info/15'}`} />
      <div className="pointer-events-none absolute right-[-5rem] top-24 h-[24rem] w-[24rem] rounded-full bg-white/30 blur-3xl" />

      {/* ── Hero section (shared layout, content from config) ── */}
      <section className="pt-32 pb-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div className="space-y-7 animate-slide-up">
            <span className="eyebrow">{config.label} workspace</span>
            <h1 className="display-title">{config.heroTitle}</h1>
            <p className="hero-copy max-w-xl">{config.heroSubtitle}</p>
            <div className="flex flex-wrap gap-4 pt-2">
              <button onClick={handleCTA} className="btn-rose text-sm">
                {config.ctaLabel}
              </button>
              <Link to="/" className="btn-secondary text-sm">
                Compare both paths
              </Link>
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              <div className={`hero-chip border ${accentChip}`}>Best photo first</div>
              <div className="hero-chip">Rewrite weak sections</div>
              <div className="hero-chip">Retest and track progress</div>
            </div>
          </div>

          <div className="space-y-4 animate-reveal stagger-3">
            <div className="marketing-panel p-4 sm:p-6">
              <HeroDemo vertical={vertical} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Vertical-specific sections ── */}
      {isDating ? (
        <>
          {/* Dating: "What makes a strong lead photo" */}
          <section className="pb-16 px-5 sm:px-8 max-w-5xl mx-auto">
            <div className="rounded-[32px] border border-rose/12 bg-rose-faint p-8 sm:p-10">
              <h2 className="font-serif text-2xl sm:text-3xl text-ink mb-6">What makes a strong lead photo</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  ['Shows genuine warmth, not just good looks', 'Warmth is what converts a swipe into a conversation. People respond to expression more than symmetry.'],
                  ['Makes someone pause mid-swipe', 'A strong lead photo breaks the scrolling pattern — eye contact, natural light, and a relaxed pose all contribute.'],
                  ['Works as a conversation starter', 'The best lead photos give the other person something to say. Context, activity, and setting do the heavy lifting.'],
                ].map(([title, detail]) => (
                  <div key={title} className="metric-tile border-rose/10">
                    <div className="text-[11px] uppercase tracking-[0.22em] mb-2 text-rose">{title}</div>
                    <div className="text-sm text-ink-muted leading-relaxed">{detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Dating: Profile preview */}
          <section className="pb-16 px-5 sm:px-8 max-w-5xl mx-auto">
            <EditorialFlow
              text="This is what a strong dating lead photo looks like inside Hinge. The warm expression and natural light create an approachable first impression that makes someone want to know more."
              font={buildFontString(400, 16, 'ui')}
              className="text-ink-muted"
            >
              <ProfilePreviewCard
                vertical="dating"
                name="Sarah, 28"
                verdict="Strong lead photo — warm expression and natural light"
                traits={[{ name: 'Attractiveness', value: 8.4 }, { name: 'Trustworthiness', value: 8.1 }]}
              />
            </EditorialFlow>
          </section>

          {/* Dating: Workflow */}
          {workflowSection}

          {/* Dating: Proof section */}
          <section className="pb-24 px-5 sm:px-8 max-w-5xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                ['Match rate lift', 'after switching to the #1 pick'],
                ['Score improvement', 'Average score improvement after one retake cycle'],
                ['3+ photos compared', 'Users who compared 3+ photos before choosing'],
              ].map(([title, detail]) => (
                <div key={title} className="metric-tile border-rose/10 text-center py-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] mb-2 text-rose">{title}</div>
                  <div className="text-sm text-ink-muted leading-relaxed">{detail}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Dating: FAQ */}
          <section className="pb-24 px-5 sm:px-8 max-w-3xl mx-auto">
            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase text-center mb-3">Common questions</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-ink text-center mb-8" style={{ fontWeight: 600 }}>
              Dating photo FAQ
            </h2>
            <VerticalFaqAccordion items={DATING_FAQ} />
          </section>
        </>
      ) : (
        <>
          {/* LinkedIn: "What makes a recruiter-ready headshot" */}
          <section className="pb-20 px-5 sm:px-8 max-w-5xl mx-auto">
            <div className="rounded-[32px] border border-info/12 bg-info/5 p-8 sm:p-12">
              <h2 className="font-serif text-2xl sm:text-3xl text-ink mb-6">What makes a recruiter-ready headshot</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  ['Projects competence in the first 100 milliseconds', 'Recruiters scan hundreds of profiles. Your headshot needs to pass a snap judgment before anyone reads a word.'],
                  ['Looks intentional, not accidental', 'A deliberate headshot signals that you take your career seriously. Cropped group photos and selfies signal the opposite.'],
                  ['Matches the seniority and role you want', 'A startup founder and a corporate VP need different visual signals. The right headshot positions you for the next role, not the last one.'],
                ].map(([title, detail]) => (
                  <div key={title} className="metric-tile border-info/10">
                    <div className="text-[11px] uppercase tracking-[0.22em] mb-2 text-info">{title}</div>
                    <div className="text-sm text-ink-muted leading-relaxed">{detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* LinkedIn: Profile preview */}
          <section className="pb-16 px-5 sm:px-8 max-w-5xl mx-auto">
            <EditorialFlow
              text="This is what a recruiter-ready headshot looks like on LinkedIn. The clean background and confident posture project competence and trust in the first 100 milliseconds of a profile scan."
              font={buildFontString(400, 16, 'ui')}
              className="text-ink-muted"
            >
              <ProfilePreviewCard
                vertical="linkedin"
                name="James Chen"
                verdict="Recruiter-ready — projects competence and trust"
                traits={[{ name: 'Competence', value: 8.6 }, { name: 'Influence', value: 7.9 }]}
              />
            </EditorialFlow>
          </section>

          {/* LinkedIn: Workflow */}
          {workflowSection}

          {/* LinkedIn: Proof section */}
          <section className="pb-28 px-5 sm:px-8 max-w-5xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                ['Profile view increase', 'after headshot upgrade'],
                ['Recruiter response rate', 'improvement with a stronger headshot'],
                ['First compare wins', 'Users who found their strongest headshot on the first compare'],
              ].map(([title, detail]) => (
                <div key={title} className="metric-tile border-info/10 text-center py-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] mb-2 text-info">{title}</div>
                  <div className="text-sm text-ink-muted leading-relaxed">{detail}</div>
                </div>
              ))}
            </div>
          </section>

          {/* LinkedIn: FAQ */}
          <section className="pb-28 px-5 sm:px-8 max-w-3xl mx-auto">
            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase text-center mb-3">Common questions</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-ink text-center mb-8" style={{ fontWeight: 600 }}>
              LinkedIn headshot FAQ
            </h2>
            <VerticalFaqAccordion items={LINKEDIN_FAQ} />
          </section>
        </>
      )}
    </div>
  );
}
